import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate } from "@/lib/utils-format";
import { Plus, CheckCircle2, XCircle, Trash2, Wallet, Banknote, Landmark, TrendingUp, Clock, ShieldCheck, Paperclip, X, FileText, Image as ImageIcon, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// cash & bank accounts can never be the party / income / expense leg of a receipt or payment
const isCashBank = (c) => c.category === "cash_bank" || ["1001", "1002", "1003"].includes(c.code) || /^1002-/.test(c.code || "");
export const voucherAccountOptions = (coa, type) => coa.filter((c) => !isCashBank(c) && (
  type === "receipt" ? ["income", "asset", "liability"].includes(c.type) : ["expense", "liability", "asset"].includes(c.type)));

const STAT_ACCENT = {
  green: "text-emerald-700",
  red: "text-red-700",
  orange: "text-[#C05621]",
  slate: "text-slate-800",
};

const Stat = ({ label, value, accent = "slate", icon: Icon, sub }) => (
  <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid={`fin-stat-${(label || "").toLowerCase().replace(/\W+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <div className="uc-label">{label}</div>
      {Icon && <Icon className="w-4 h-4 text-[#C05621]" />}
    </div>
    <div className={`font-[Outfit] text-2xl font-semibold mt-1 tracking-tight ${STAT_ACCENT[accent]}`}>{value}</div>
    {sub && <div className="text-xs text-[#8B7355] mt-1">{sub}</div>}
  </div>
);

// ==============================================================
// DASHBOARD PANEL
// ==============================================================
export function FinanceDashboardPanel() {
  const [d, setD] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/finance/dashboard");
      setD(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  if (!d) return <div className="text-[#8B7355] text-sm">Loading…</div>;

  const methodEntries = Object.entries(d.collections_by_method || {});
  return (
    <div className="space-y-6" data-testid="fin-dashboard-panel">
      <div className="text-xs text-[#8B7355]">Live for <b>{fmtDate(d.today)}</b> · refreshes every 30s</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Today's Sales" value={inr(d.today_sales)} accent="green" icon={TrendingUp}
              sub={`${d.today_sales_count} invoice${d.today_sales_count === 1 ? "" : "s"}`} />
        <Stat label="Today's Collections" value={inr(d.today_collections)} accent="orange" icon={Banknote}
              sub={methodEntries.length ? methodEntries.map(([m, v]) => `${m}:${inr(v)}`).join(" · ") : "No payments yet"} />
        <Stat label="Cash In Hand" value={inr(d.cash_balance)} accent="slate" icon={Wallet}
              sub="Account 1001" />
        <Stat label="UPI / Wallets" value={inr(d.upi_balance)} accent="slate" icon={Wallet}
              sub="Account 1003" />
      </div>

      <div>
        <div className="uc-label mb-2">Bank balances</div>
        {(d.bank_balances || []).length === 0 && (
          <div className="bg-[#FEEBC8] border border-[#F6AD55] rounded-md p-4 text-sm text-[#7B341E]">
            No bank accounts configured. Add one under <b>Bank Accounts</b> tab.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(d.bank_balances || []).map((b) => (
            <div key={b.id} className="bg-white border border-[#EAE5D9] rounded-lg p-4" data-testid={`fin-bank-stat-${b.id}`}>
              <div className="flex items-center gap-2 text-sm">
                <Landmark className="w-4 h-4 text-[#C05621]" />
                <span className="font-medium">{b.name}</span>
                <span className="ml-auto text-[10px] font-mono text-[#8B7355]">{b.account_code}</span>
              </div>
              <div className="font-[Outfit] text-xl font-semibold mt-1">{inr(b.balance)}</div>
            </div>
          ))}
        </div>
        {(d.bank_balances || []).length > 0 && (
          <div className="text-xs text-[#8B7355] mt-2">Total bank balance: <b className="text-slate-800">{inr(d.total_bank_balance)}</b></div>
        )}
      </div>

      {d.pending_approvals?.total > 0 && (
        <div className="space-y-3">
          <div className="bg-[#FEEBC8] border border-[#F6AD55] rounded-md p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-[#7B341E]" />
            <div className="flex-1 text-sm text-[#7B341E]">
              <b>{d.pending_approvals.total}</b> pending approvals
              {d.pending_approvals.vouchers > 0 && ` · ${d.pending_approvals.vouchers} voucher(s)`}
              {d.pending_approvals.expenses > 0 && ` · ${d.pending_approvals.expenses} expense(s)`}
            </div>
          </div>
          {(d.pending_approvals.vouchers_list?.length || d.pending_approvals.expenses_list?.length) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fin-dash-pending">
              <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
                <div className="px-4 py-2 text-xs uppercase tracking-wide text-[#8B7355] border-b border-[#EAE5D9] bg-[#FAF6EE]">Pending vouchers · latest 5</div>
                {(d.pending_approvals.vouchers_list || []).length === 0 && <div className="p-3 text-xs text-[#8B7355]">None 🎉</div>}
                {(d.pending_approvals.vouchers_list || []).map((v) => (
                  <div key={v.id} className="px-4 py-2 border-b border-[#EAE5D9] last:border-0 flex items-center gap-2 text-sm" data-testid={`fin-dash-pending-v-${v.id}`}>
                    <span className="font-mono text-[10px] text-[#8B7355]">{v.number}</span>
                    <Badge variant="outline" className="capitalize text-[10px]">{v.type}</Badge>
                    <span className="flex-1 truncate">{v.party?.name || v.narration}</span>
                    {v.has_attachment && <Paperclip className="w-3 h-3 text-[#C05621]" title="Has attachment" />}
                    <span className="font-semibold">{inr(v.amount)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
                <div className="px-4 py-2 text-xs uppercase tracking-wide text-[#8B7355] border-b border-[#EAE5D9] bg-[#FAF6EE]">Pending expenses · latest 5</div>
                {(d.pending_approvals.expenses_list || []).length === 0 && <div className="p-3 text-xs text-[#8B7355]">None 🎉</div>}
                {(d.pending_approvals.expenses_list || []).map((e) => (
                  <div key={e.id} className="px-4 py-2 border-b border-[#EAE5D9] last:border-0 flex items-center gap-2 text-sm" data-testid={`fin-dash-pending-e-${e.id}`}>
                    <Badge variant="outline" className="capitalize text-[10px]">{e.category}</Badge>
                    <span className="flex-1 truncate">{e.description}</span>
                    {e.has_attachment && <Paperclip className="w-3 h-3 text-[#C05621]" title="Has attachment" />}
                    <span className="font-semibold text-red-700">{inr(e.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ==============================================================
// VOUCHERS PANEL
// ==============================================================
const VOUCHER_TYPES = [
  { k: "payment", label: "Payment Voucher" },
  { k: "receipt", label: "Receipt Voucher" },
  { k: "journal", label: "Journal Voucher" },
  { k: "bill",    label: "Purchase Bill" },
];
const MODES = [
  { k: "cash", label: "Cash" },
  { k: "bank", label: "Bank" },
  { k: "upi",  label: "UPI / Digital" },
];

const emptyVoucher = {
  type: "payment", voucher_date: "", amount: "", narration: "",
  mode: "cash", bank_account_id: "", account_code: "", account_name: "",
  party: { name: "", type: "supplier" }, reference: "", lines: [],
  attachment_url: null, attachment_name: null, attachment_mime: null,
};

const emptyJLine = { account_code: "", account_name: "", debit: 0, credit: 0, notes: "" };

export function VouchersPanel({ isChief }) {
  const { user } = useAuth();
  const isSuper = (user?.role_id || user?.role) === "super_admin";
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyVoucher);
  const [coa, setCoa] = useState([]);
  const [banks, setBanks] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.type = typeFilter;
    const [v, c, b] = await Promise.all([
      api.get("/finance/vouchers", { params }),
      api.get("/accounts/coa"),
      api.get("/finance/bank-accounts"),
    ]);
    setRows(v.data); setCoa(c.data); setBanks(b.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, typeFilter]);

  const startNew = () => { setForm({ ...emptyVoucher, lines: [{ ...emptyJLine }, { ...emptyJLine }] }); setOpen(true); };

  const setLine = (i, k, v) => {
    setForm((f) => ({ ...f, lines: f.lines.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln) }));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...emptyJLine }] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const jTotals = useMemo(() => {
    const dr = (form.lines || []).reduce((s, l) => s + Number(l.debit || 0), 0);
    const cr = (form.lines || []).reduce((s, l) => s + Number(l.credit || 0), 0);
    return { dr: Number(dr.toFixed(2)), cr: Number(cr.toFixed(2)), balanced: Math.abs(dr - cr) < 0.05 && dr > 0 };
  }, [form.lines]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (form.type === "journal") {
        if (!jTotals.balanced) { toast.error("Journal not balanced"); setBusy(false); return; }
        payload.lines = form.lines.map((l) => ({
          account_code: l.account_code,
          account_name: l.account_name || (coa.find((c) => c.code === l.account_code)?.name || ""),
          debit: Number(l.debit || 0), credit: Number(l.credit || 0), notes: l.notes,
        }));
        payload.amount = jTotals.dr;
      } else {
        payload.amount = Number(form.amount || 0);
        if (payload.amount <= 0) { toast.error("Amount must be > 0"); setBusy(false); return; }
        if (!payload.account_code) { toast.error("Please pick an account"); setBusy(false); return; }
        payload.account_name = coa.find((c) => c.code === payload.account_code)?.name || "";
        if (payload.bank_account_id === "__none__" || payload.bank_account_id === "") payload.bank_account_id = null;
      }
      const { data } = await api.post("/finance/vouchers", payload);
      toast.success(`${data.number} — ${data.status === "approved" ? "posted to journal" : "sent for approval"}`);
      setOpen(false);
      setForm(emptyVoucher);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doApprove = async (v) => {
    try { await api.post(`/finance/vouchers/${v.id}/approve`); toast.success(`${v.number} approved & posted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/finance/vouchers/${rejectOpen.id}/reject`, { reason: rejectReason });
      toast.success(`${rejectOpen.number} rejected`);
      setRejectOpen(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fin-vouchers-panel">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="fin-vouchers-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All types</SelectItem>
              {VOUCHER_TYPES.map((t) => <SelectItem key={t.k} value={t.k}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="fin-vouchers-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          <Button onClick={startNew} data-testid="fin-voucher-new-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
            <Plus className="w-4 h-4 mr-1" /> New Voucher
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left uc-label border-b border-[#EAE5D9]">
            <th className="px-4 py-3">Number</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Party</th>
            <th className="px-4 py-3">Narration</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Actions</th>
          </tr></thead>
          <tbody data-testid="fin-vouchers-rows">
            {rows.map((v) => (
              <tr key={v.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7] cursor-pointer" data-testid={`fin-voucher-row-${v.id}`} onClick={() => setDetail(v)}>
                <td className="px-4 py-2 font-mono text-xs text-[#C05621] underline-offset-2 hover:underline">{v.number}</td>
                <td className="px-4 py-2 text-[#8B7355]">{fmtDate(v.voucher_date)}</td>
                <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{v.type}</Badge></td>
                <td className="px-4 py-2">{v.party?.name || "—"}</td>
                <td className="px-4 py-2 text-[#5C4033] max-w-[300px] truncate">
                  {v.narration}
                  {v.attachment_url && <AttachmentPreview url={v.attachment_url} name={v.attachment_name} mime={v.attachment_mime} className="ml-2" />}
                </td>
                <td className="px-4 py-2 text-right font-semibold">{inr(v.amount)}</td>
                <td className="px-4 py-2"><StatusBadge status={v.status} /></td>
                <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                  {v.status === "pending" && isChief && (
                    <div className="flex gap-1">
                      <button onClick={() => doApprove(v)} data-testid={`fin-voucher-approve-${v.id}`}
                              className="text-emerald-700 hover:bg-emerald-50 rounded p-1"><CheckCircle2 className="w-4 h-4" /></button>
                      <button onClick={() => { setRejectOpen(v); setRejectReason(""); }} data-testid={`fin-voucher-reject-${v.id}`}
                              className="text-red-600 hover:bg-red-50 rounded p-1"><XCircle className="w-4 h-4" /></button>
                    </div>
                  )}
                  {v.status === "pending" && !isChief && (
                    <span className="text-xs text-[#8B7355] italic">Awaiting Chief</span>
                  )}
                  {v.status === "rejected" && v.rejection_reason && (
                    <span className="text-xs text-red-600 italic" title={v.rejection_reason}>Reason: {v.rejection_reason.slice(0, 24)}{v.rejection_reason.length > 24 ? "…" : ""}</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#8B7355]">No vouchers</td></tr>}
          </tbody>
        </table>
      </div>

      <VoucherDetailDialog voucher={detail} onClose={() => setDetail(null)} isSuper={isSuper} onChanged={load} />

      {/* Create voucher dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">New Voucher</DialogTitle>
            <DialogDescription>Fill in the details. {isChief ? "It will be posted to the journal immediately." : "It will be sent to Chief Accountant for approval."}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Voucher Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, lines: v === "journal" ? [{ ...emptyJLine }, { ...emptyJLine }] : [] })}>
                  <SelectTrigger data-testid="fin-v-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {VOUCHER_TYPES.map((t) => <SelectItem key={t.k} value={t.k}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Voucher Date</Label><Input type="date" value={form.voucher_date} onChange={(e) => setForm({ ...form, voucher_date: e.target.value })} data-testid="fin-v-date" /></div>
            </div>

            {form.type !== "journal" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fin-v-amount" /></div>
                  <div>
                    <Label>{form.type === "bill" ? "Contra: Accounts Payable (auto)" : "Payment mode"}</Label>
                    {form.type === "bill" ? (
                      <Input value="Accounts Payable (2001)" disabled />
                    ) : (
                      <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v, bank_account_id: v === "bank" ? form.bank_account_id : "" })}>
                        <SelectTrigger data-testid="fin-v-mode"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {MODES.map((m) => <SelectItem key={m.k} value={m.k}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                {form.mode === "bank" && form.type !== "bill" && (
                  <div>
                    <Label>Bank Account</Label>
                    <Select value={form.bank_account_id || "__none__"} onValueChange={(v) => setForm({ ...form, bank_account_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="fin-v-bank"><SelectValue placeholder="Pick a bank" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="__none__">— (use default Bank 1002)</SelectItem>
                        {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.account_code}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>{form.type === "payment" ? "Expense / Party account" : form.type === "receipt" ? "Income / Party account" : "Purchase / Expense account"}</Label>
                  <Select value={form.account_code || "__none__"} onValueChange={(v) => setForm({ ...form, account_code: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="fin-v-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">
                      <SelectItem value="__none__">— Select —</SelectItem>
                      {voucherAccountOptions(coa, form.type).map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.code} · {c.name} <span className="text-[10px] uppercase text-[#8B7355] ml-1">{c.type}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Party name</Label><Input value={form.party?.name || ""} onChange={(e) => setForm({ ...form, party: { ...form.party, name: e.target.value } })} data-testid="fin-v-party" /></div>
                  <div><Label>Reference / Bill No.</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="fin-v-ref" /></div>
                </div>
              </>
            )}

            {form.type === "journal" && (
              <div className="space-y-2">
                <div className="uc-label">Ledger lines (must balance)</div>
                <div className="border border-[#EAE5D9] rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAF6EE] text-[10px] uppercase text-[#8B7355]">
                      <tr>
                        <th className="text-left px-2 py-2">Account</th>
                        <th className="text-right px-2 py-2 w-32">Debit</th>
                        <th className="text-right px-2 py-2 w-32">Credit</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody data-testid="fin-jrows">
                      {form.lines.map((l, i) => (
                        <tr key={i} className="border-t border-[#EAE5D9]">
                          <td className="px-2 py-1">
                            <Select value={l.account_code || "__none__"} onValueChange={(v) => setLine(i, "account_code", v === "__none__" ? "" : v)}>
                              <SelectTrigger data-testid={`fin-jline-acc-${i}`}><SelectValue placeholder="Pick account" /></SelectTrigger>
                              <SelectContent className="bg-white max-h-72">
                                <SelectItem value="__none__">— Select —</SelectItem>
                                {coa.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1"><Input type="number" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} data-testid={`fin-jline-dr-${i}`} /></td>
                          <td className="px-2 py-1"><Input type="number" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} data-testid={`fin-jline-cr-${i}`} /></td>
                          <td className="px-2 py-1 text-center">
                            {form.lines.length > 2 && <button onClick={() => rmLine(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-[#FAF6EE] border-t-2 border-[#2C1810]">
                        <td className="px-2 py-2 font-semibold">Totals</td>
                        <td className="px-2 py-2 text-right font-semibold">{inr(jTotals.dr)}</td>
                        <td className="px-2 py-2 text-right font-semibold">{inr(jTotals.cr)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between">
                  <button onClick={addLine} className="text-sm text-[#C05621] hover:underline flex items-center gap-1" data-testid="fin-jline-add"><Plus className="w-3 h-3" /> Add line</button>
                  <div className={`text-xs font-semibold ${jTotals.balanced ? "text-emerald-700" : "text-red-600"}`}>
                    {jTotals.balanced ? "Balanced ✓" : `Not balanced — diff ${inr(Math.abs(jTotals.dr - jTotals.cr))}`}
                  </div>
                </div>
              </div>
            )}

            <div><Label>Narration</Label><Input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} data-testid="fin-v-narration" /></div>

            <AttachmentInput
              label="Bill photo / PDF"
              hint="Optional — attach a picture of the bill or a PDF (max 6 MB)."
              value={{ url: form.attachment_url, name: form.attachment_name, mime: form.attachment_mime }}
              onChange={(a) => setForm({ ...form, attachment_url: a?.url || null, attachment_name: a?.name || null, attachment_mime: a?.mime || null })}
              testId="fin-v-attach"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="fin-v-save" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              {busy ? "Saving…" : (isChief ? "Save & Post" : "Send for Approval")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectOpen} onOpenChange={(o) => !o && setRejectOpen(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Reject Voucher {rejectOpen?.number}</DialogTitle>
            <DialogDescription>Assistant will be able to edit and resubmit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Rejection reason *</Label>
            <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="fin-reject-reason" placeholder="e.g. wrong account code" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Cancel</Button>
            <Button onClick={doReject} data-testid="fin-reject-confirm" className="bg-red-600 hover:bg-red-700 text-white">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "approved") return <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Approved</Badge>;
  if (status === "rejected") return <Badge className="bg-red-600 hover:bg-red-700 text-white">Rejected</Badge>;
  if (status === "pending")  return <Badge className="bg-amber-500 hover:bg-amber-600 text-white">Pending</Badge>;
  if (status === "reversed") return <Badge className="bg-slate-600 hover:bg-slate-700 text-white">Reversed</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}
export { StatusBadge };

// ==============================================================
// VOUCHER DETAIL — ledger legs + Super Admin Reverse / Delete
// ==============================================================
function VoucherDetailDialog({ voucher: v, onClose, isSuper, onChanged }) {
  const [confirm, setConfirm] = useState(null); // "reverse" | "delete"
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setConfirm(null); setReason(""); }, [v?.id]);
  if (!v) return null;
  const canGovern = isSuper && ["approved", "reversed", "pending", "rejected"].includes(v.status);
  const run = async () => {
    setBusy(true);
    try {
      if (confirm === "reverse") {
        await api.post(`/finance/vouchers/${v.id}/reverse`, { reason: reason || "Reversed by HO Super Admin" });
        toast.success(`${v.number} reversed — opposite entry posted today`);
      } else {
        const { data } = await api.delete(`/finance/vouchers/${v.id}`);
        toast.success(`${v.number} deleted (${data.journal_entries_removed} journal entr${data.journal_entries_removed === 1 ? "y" : "ies"} removed)`);
      }
      onClose(); await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!v} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="fin-voucher-detail">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <span className="font-mono">{v.number}</span>
            <Badge variant="outline" className="capitalize">{v.type}</Badge>
            <StatusBadge status={v.status} />
          </DialogTitle>
          <DialogDescription>
            {fmtDate(v.voucher_date)} · {v.party?.name ? `Party: ${v.party.name} · ` : ""}{v.mode ? `Mode: ${v.mode}` : ""}
            {v.reference ? ` · Ref: ${v.reference}` : ""}
          </DialogDescription>
        </DialogHeader>
        {v.narration && <p className="text-sm text-[#5C4033]" data-testid="fin-voucher-detail-narration">{v.narration}</p>}
        <table className="w-full text-sm border border-[#EAE5D9] rounded">
          <thead><tr className="uc-label text-left border-b border-[#EAE5D9]">
            <th className="px-3 py-2">Account</th><th className="px-3 py-2 text-right">Debit</th><th className="px-3 py-2 text-right">Credit</th>
          </tr></thead>
          <tbody data-testid="fin-voucher-detail-lines">
            {(v.lines || []).map((l, i) => (
              <tr key={i} className="border-b border-[#EAE5D9] last:border-0">
                <td className="px-3 py-2"><span className="font-mono text-xs text-[#8B7355] mr-2">{l.account_code}</span>{l.account_name}{l.party ? <span className="text-xs text-[#8B7355]"> · {l.party}</span> : null}</td>
                <td className="px-3 py-2 text-right">{Number(l.debit) > 0 ? inr(l.debit) : "—"}</td>
                <td className="px-3 py-2 text-right">{Number(l.credit) > 0 ? inr(l.credit) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-xs text-[#8B7355] space-y-0.5">
          <div>Submitted by {v.submitted_by_name || "—"}{v.approved_by_name ? ` · Approved by ${v.approved_by_name}` : ""}</div>
          {v.reversed_at && <div className="text-slate-700" data-testid="fin-voucher-detail-reversed">Reversed {fmtDate(v.reversed_at)} by {v.reversed_by_name} · {v.reversal_reason}</div>}
          {v.rejection_reason && <div className="text-red-600">Rejected: {v.rejection_reason}</div>}
        </div>
        {canGovern && !confirm && (
          <DialogFooter className="gap-2">
            {v.status === "approved" && (
              <Button variant="outline" onClick={() => setConfirm("reverse")} data-testid="fin-voucher-reverse-btn" className="border-slate-400">
                <XCircle className="w-4 h-4 mr-1" /> Reverse
              </Button>
            )}
            <Button variant="outline" onClick={() => setConfirm("delete")} data-testid="fin-voucher-delete-btn" className="border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        )}
        {confirm && (
          <div className={`rounded-lg border p-3 space-y-2 ${confirm === "delete" ? "border-red-300 bg-red-50" : "border-slate-300 bg-slate-50"}`} data-testid="fin-voucher-govern-confirm">
            <div className="text-sm font-medium">
              {confirm === "delete"
                ? `Permanently delete ${v.number} and its journal entry? This cannot be undone — nothing remains in the books.`
                : `Reverse ${v.number}? An equal and opposite journal entry dated today will be posted; the original stays for audit.`}
            </div>
            {confirm === "reverse" && <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="fin-voucher-reverse-reason" />}
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy} data-testid="fin-voucher-govern-cancel">Cancel</Button>
              <Button onClick={run} disabled={busy} data-testid="fin-voucher-govern-confirm-btn"
                      className={confirm === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-slate-700 hover:bg-slate-800 text-white"}>
                {busy ? "Working…" : confirm === "delete" ? "Delete permanently" : "Post reversal"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==============================================================
// SHARED — Attachment upload (image or PDF, ≤6 MB, stored as data: URI)
// ==============================================================
const MAX_ATTACH_BYTES = 6_000_000;
export function AttachmentInput({ label = "Attachment", hint, value, onChange, testId = "attach" }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_ATTACH_BYTES) return toast.error(`File too large — max ${MAX_ATTACH_BYTES / 1_000_000} MB`);
    if (!(f.type.startsWith("image/") || f.type === "application/pdf")) {
      return toast.error("Only image or PDF files are allowed");
    }
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(new Error("Read failed"));
        r.readAsDataURL(f);
      });
      onChange({ url: b64, name: f.name, mime: f.type });
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <Label>{label}</Label>
      {value?.url ? (
        <div className="flex items-center gap-2 border border-[#EAE5D9] rounded-md p-2 bg-[#FAF6EE]" data-testid={`${testId}-preview`}>
          {value.mime?.startsWith("image/") ? (
            <img src={value.url} alt="" className="w-12 h-12 object-cover rounded" />
          ) : (
            <div className="w-12 h-12 rounded bg-white border border-[#EAE5D9] flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#C05621]" />
            </div>
          )}
          <div className="flex-1 text-xs">
            <div className="font-medium truncate">{value.name || "attachment"}</div>
            <div className="text-[#8B7355]">{value.mime}</div>
          </div>
          <button type="button" onClick={() => onChange(null)} data-testid={`${testId}-remove`} className="text-red-500 hover:text-red-600 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className="flex items-center gap-2 border border-dashed border-[#C05621] rounded-md p-3 cursor-pointer hover:bg-[#FEEBC8] transition text-sm" data-testid={`${testId}-picker`}>
          <Paperclip className="w-4 h-4 text-[#C05621]" />
          <span className="text-[#5C4033]">{busy ? "Reading…" : "Click to attach photo or PDF"}</span>
          <input type="file" accept="image/*,application/pdf" onChange={onPick} className="hidden" data-testid={`${testId}-input`} />
        </label>
      )}
      {hint && <div className="text-[10px] text-[#8B7355] mt-1">{hint}</div>}
    </div>
  );
}

export function AttachmentPreview({ url, name, mime, className = "" }) {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  const isImg = (mime || "").startsWith("image/");
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid="attach-view" title={name || "View attachment"}
              className={`inline-flex items-center gap-1 text-[10px] text-[#C05621] hover:text-[#9C4221] hover:underline ${className}`}>
        <Paperclip className="w-3 h-3" /> Bill
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              {isImg ? <ImageIcon className="w-5 h-5 text-[#C05621]" /> : <FileText className="w-5 h-5 text-[#C05621]" />}
              {name || "Bill attachment"}
              <a href={url} download={name || "attachment"} className="ml-auto text-xs inline-flex items-center gap-1 text-[#C05621] hover:underline">
                <Download className="w-3 h-3" /> Download
              </a>
            </DialogTitle>
            <DialogDescription className="sr-only">Preview of the attached bill.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[75vh] overflow-auto bg-[#FAF6EE] rounded-md p-2">
            {isImg ? (
              <img src={url} alt={name || ""} className="w-full h-auto rounded" />
            ) : (
              <iframe src={url} title={name || "PDF"} className="w-full h-[70vh] bg-white rounded" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ==============================================================
// BANK ACCOUNTS PANEL
// ==============================================================
const emptyBank = { name: "", bank_name: "", account_number_last4: "", ifsc: "", opening_balance: 0, opening_date: "" };
export function BankAccountsPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyBank);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/finance/bank-accounts");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post("/finance/bank-accounts", { ...form, opening_balance: Number(form.opening_balance || 0) });
      toast.success("Bank account added");
      setOpen(false); setForm(emptyBank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.name}?`)) return;
    try { await api.delete(`/finance/bank-accounts/${b.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fin-banks-panel">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} data-testid="fin-bank-new" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
          <Plus className="w-4 h-4 mr-1" /> Add Bank Account
        </Button>
      </div>
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left uc-label border-b border-[#EAE5D9]">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Bank</th>
            <th className="px-4 py-3">A/c ****</th>
            <th className="px-4 py-3">IFSC</th>
            <th className="px-4 py-3">Ledger code</th>
            <th className="px-4 py-3 text-right">Opening</th>
            <th className="px-4 py-3 text-right">Balance</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`fin-bank-row-${b.id}`}>
                <td className="px-4 py-2 font-medium">{b.name}</td>
                <td className="px-4 py-2">{b.bank_name || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs">****{b.account_number_last4 || "----"}</td>
                <td className="px-4 py-2 font-mono text-xs">{b.ifsc || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs">{b.account_code}</td>
                <td className="px-4 py-2 text-right">{inr(b.opening_balance)}</td>
                <td className="px-4 py-2 text-right font-semibold">{inr(b.balance)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => remove(b)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#8B7355]">No bank accounts yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Add Bank Account</DialogTitle>
            <DialogDescription>A new ledger code will be auto-created under Assets (1002-XX).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Display name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fin-bank-name" placeholder="HDFC Main OD" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bank</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="fin-bank-bankname" /></div>
              <div><Label>Last 4 of A/c</Label><Input value={form.account_number_last4} onChange={(e) => setForm({ ...form, account_number_last4: e.target.value })} data-testid="fin-bank-last4" /></div>
            </div>
            <div><Label>IFSC</Label><Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} data-testid="fin-bank-ifsc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Opening balance (₹)</Label><Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="fin-bank-open-bal" /></div>
              <div><Label>Opening date</Label><Input type="date" value={form.opening_date} onChange={(e) => setForm({ ...form, opening_date: e.target.value })} data-testid="fin-bank-open-date" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="fin-bank-save" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==============================================================
// APPROVALS QUEUE
// ==============================================================
export function ApprovalsPanel({ isChief, onChanged }) {
  const [data, setData] = useState({ vouchers: [], expenses: [], counts: { total: 0 } });
  const [rejectOpen, setRejectOpen] = useState(null); // { kind, item }
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    const { data } = await api.get("/finance/approvals/pending");
    setData(data);
    onChanged?.(data.counts?.total || 0);
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);

  const approveVoucher = async (v) => {
    try { await api.post(`/finance/vouchers/${v.id}/approve`); toast.success(`${v.number} approved`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approveExpense = async (e) => {
    try { await api.post(`/expenses/${e.id}/approve`); toast.success("Expense approved"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error("Reason required");
    try {
      const path = rejectOpen.kind === "voucher"
        ? `/finance/vouchers/${rejectOpen.item.id}/reject`
        : `/expenses/${rejectOpen.item.id}/reject`;
      await api.post(path, { reason: rejectReason });
      toast.success("Rejected");
      setRejectOpen(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="fin-approvals-panel">
      {!isChief && (
        <div className="bg-[#FEEBC8] border border-[#F6AD55] rounded-md p-3 text-sm text-[#7B341E] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> You have view-only access. Only Chief Accountants (or Super Admin) can approve/reject.
        </div>
      )}

      <div>
        <h3 className="font-[Outfit] text-lg font-semibold mb-2">Pending Vouchers · {data.vouchers.length}</h3>
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Number</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Submitted by</th>
              <th className="px-4 py-3">Party</th>
              <th className="px-4 py-3">Narration</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {data.vouchers.map((v) => (
                <tr key={v.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`fin-appr-voucher-${v.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{v.number}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{v.type}</Badge></td>
                  <td className="px-4 py-2 text-xs">{v.submitted_by_name}</td>
                  <td className="px-4 py-2">{v.party?.name || "—"}</td>
                  <td className="px-4 py-2 text-[#5C4033] max-w-[260px] truncate">
                    {v.narration}
                    {v.attachment_url && <AttachmentPreview url={v.attachment_url} name={v.attachment_name} mime={v.attachment_mime} className="ml-2" />}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">{inr(v.amount)}</td>
                  <td className="px-4 py-2">
                    {isChief ? (
                      <div className="flex gap-1">
                        <button onClick={() => approveVoucher(v)} data-testid={`fin-appr-approve-${v.id}`} className="text-emerald-700 hover:bg-emerald-50 rounded p-1"><CheckCircle2 className="w-4 h-4" /></button>
                        <button onClick={() => { setRejectOpen({ kind: "voucher", item: v }); setRejectReason(""); }} data-testid={`fin-appr-reject-${v.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><XCircle className="w-4 h-4" /></button>
                      </div>
                    ) : <span className="text-xs text-[#8B7355]">—</span>}
                  </td>
                </tr>
              ))}
              {data.vouchers.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[#8B7355]">No pending vouchers 🎉</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="font-[Outfit] text-lg font-semibold mb-2">Pending Expenses · {data.expenses.length}</h3>
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Submitted by</th>
              <th className="px-4 py-3">Paid to</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {data.expenses.map((e) => (
                <tr key={e.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`fin-appr-expense-${e.id}`}>
                  <td className="px-4 py-2 text-[#8B7355]">{fmtDate(e.date)}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{e.category}</Badge></td>
                  <td className="px-4 py-2">{e.description}</td>
                  <td className="px-4 py-2 text-xs">{e.submitted_by_name}</td>
                  <td className="px-4 py-2 text-[#5C4033]">
                    {e.paid_to || "—"}
                    {e.attachment_url && <AttachmentPreview url={e.attachment_url} name={e.attachment_name} mime={e.attachment_mime} className="ml-2" />}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">{inr(e.amount)}</td>
                  <td className="px-4 py-2">
                    {isChief ? (
                      <div className="flex gap-1">
                        <button onClick={() => approveExpense(e)} data-testid={`fin-appr-exp-approve-${e.id}`} className="text-emerald-700 hover:bg-emerald-50 rounded p-1"><CheckCircle2 className="w-4 h-4" /></button>
                        <button onClick={() => { setRejectOpen({ kind: "expense", item: e }); setRejectReason(""); }} data-testid={`fin-appr-exp-reject-${e.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><XCircle className="w-4 h-4" /></button>
                      </div>
                    ) : <span className="text-xs text-[#8B7355]">—</span>}
                  </td>
                </tr>
              ))}
              {data.expenses.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[#8B7355]">No pending expenses 🎉</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!rejectOpen} onOpenChange={(o) => !o && setRejectOpen(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Reject {rejectOpen?.kind === "voucher" ? rejectOpen?.item?.number : "expense"}</DialogTitle>
            <DialogDescription className="sr-only">Provide a reason for rejection so the maker can amend and resubmit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Rejection reason *</Label>
            <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="fin-appr-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Cancel</Button>
            <Button onClick={doReject} data-testid="fin-appr-reject-confirm" className="bg-red-600 hover:bg-red-700 text-white">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==============================================================
// DAY BOOK — chronological view of every APPROVED transaction for a day
// ==============================================================
const KIND_STYLES = {
  sale:     { label: "POS Sale",     cls: "bg-emerald-100 text-emerald-800" },
  receipt:  { label: "Receipt",      cls: "bg-emerald-100 text-emerald-800" },
  payment:  { label: "Payment",      cls: "bg-red-100 text-red-800" },
  bill:     { label: "Bill",         cls: "bg-red-100 text-red-800" },
  expense:  { label: "Expense",      cls: "bg-red-100 text-red-800" },
  journal:  { label: "Journal Adj.", cls: "bg-slate-200 text-slate-800" },
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return String(iso).slice(11, 16); }
};

export function DayBookPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/daybook", { params: { date } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Day Book");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const filtered = (data?.entries || []).filter((e) => kindFilter === "all" || e.kind === kindFilter);
  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="fin-daybook-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Business date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="fin-daybook-date" className="w-44" />
        </div>
        <div>
          <Label>Kind</Label>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="w-44" data-testid="fin-daybook-kind"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All kinds</SelectItem>
              <SelectItem value="sale">POS Sales</SelectItem>
              <SelectItem value="receipt">Receipts</SelectItem>
              <SelectItem value="payment">Payments</SelectItem>
              <SelectItem value="bill">Bills</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
              <SelectItem value="journal">Journal Adjustments</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setDate(today)} variant="outline" data-testid="fin-daybook-today" className="border-[#EAE5D9]">Today</Button>
        <div className="ml-auto text-xs text-[#8B7355]">
          {loading ? "Loading…" : `${data?.count ?? 0} transaction${data?.count === 1 ? "" : "s"} · ${filtered.length} shown`}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Money in</div>
          <div className="font-[Outfit] text-xl font-semibold text-emerald-700 mt-1">{inr(t.total_credit)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">POS Sales + Receipt Vouchers</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Money out</div>
          <div className="font-[Outfit] text-xl font-semibold text-red-700 mt-1">{inr(t.total_debit)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">Payments + Bills + Expenses + Journal DR</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Net cash-flow</div>
          <div className={`font-[Outfit] text-xl font-semibold mt-1 ${(t.net_cashflow || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>{inr(t.net_cashflow)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">Credit − Debit</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="uc-label">Breakdown</div>
          <div className="text-xs mt-1 space-y-0.5 text-[#5C4033]">
            <div>Sales <b>{inr(t.sale_credit)}</b></div>
            <div>Payments <b>{inr(t.payment_debit)}</b></div>
            <div>Expenses <b>{inr(t.expense_debit)}</b></div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3 w-20">Time</th>
              <th className="px-4 py-3 w-32">Kind</th>
              <th className="px-4 py-3 w-32">Number</th>
              <th className="px-4 py-3">Party</th>
              <th className="px-4 py-3">Narration</th>
              <th className="px-4 py-3 text-right w-28">Debit</th>
              <th className="px-4 py-3 text-right w-28">Credit</th>
            </tr>
          </thead>
          <tbody data-testid="fin-daybook-rows">
            {filtered.map((e) => {
              const ks = KIND_STYLES[e.kind] || { label: e.kind, cls: "bg-slate-100 text-slate-700" };
              return (
                <tr key={`${e.kind}-${e.id}`} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`fin-daybook-row-${e.id}`}>
                  <td className="px-4 py-2 text-[#8B7355] font-mono text-xs">{fmtTime(e.when)}</td>
                  <td className="px-4 py-2"><span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded ${ks.cls} font-medium`}>{ks.label}</span></td>
                  <td className="px-4 py-2 font-mono text-xs">{e.number}</td>
                  <td className="px-4 py-2">{e.party}</td>
                  <td className="px-4 py-2 text-[#5C4033] max-w-[400px] truncate">
                    {e.narration}
                    {e.has_attachment && <Paperclip className="inline w-3 h-3 text-[#C05621] ml-1" title="Has attachment" />}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-red-700">{e.debit > 0 ? inr(e.debit) : "—"}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-emerald-700">{e.credit > 0 ? inr(e.credit) : "—"}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[#8B7355]">
                {loading ? "Loading…" : "No approved transactions on this day"}
              </td></tr>
            )}
            {filtered.length > 0 && (
              <tr className="bg-[#FAF6EE] border-t-2 border-[#2C1810] font-semibold">
                <td colSpan={5} className="px-4 py-2 text-right uc-label">Totals</td>
                <td className="px-4 py-2 text-right font-mono text-red-700">{inr(filtered.reduce((s, x) => s + (x.debit || 0), 0))}</td>
                <td className="px-4 py-2 text-right font-mono text-emerald-700">{inr(filtered.reduce((s, x) => s + (x.credit || 0), 0))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
